import { useState, useEffect, useMemo, useCallback } from 'react'
import AnalyticsCard from '../widgetY'
import OrdersDueTable from './ordersDueTable'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { formatDefPayload, getFromStorage, saveToStorage } from '@/lib/utils'
import {
  fetchOrdersClosedPerWeek,
  fetchSitesCompletedPerWeek,
} from '@/services/apis/api'
import { toast } from 'sonner'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import { useNavigate } from 'react-router-dom'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import SortableTabCard from '../sortableTabCard'

const SUMMARY_CARD_IDS = ['orders-closed', 'sites-completed', 'orders-due']

const SummaryTab = ({
  isWidgetVisible,
}: {
  isWidgetVisible: (key: string) => boolean
}) => {
  const navigate = useNavigate()
  const [summaryCards, setSummaryCards] = useState(() => {
    const saved = getFromStorage('service_summaryCards')
    return Array.isArray(saved) ? saved : SUMMARY_CARD_IDS
  })

  useEffect(() => {
    saveToStorage('service_summaryCards', summaryCards)
  }, [summaryCards])

  const [ordersData, setOrdersData] = useState<
    { label?: string; value: number }[]
  >([])
  const [sitesData, setSitesData] = useState<
    { label?: string; value: number }[]
  >([])
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [loadingSites, setLoadingSites] = useState(false)

  const defPayload = useAppSelector(state => state.menu.payloadDef)
  const essentialIds = useMemo(() => formatDefPayload(defPayload), [defPayload])
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const handleSummaryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setSummaryCards(items => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const getOrdersData = useCallback(async () => {
    setLoadingOrders(true)
    try {
      const res = (await fetchOrdersClosedPerWeek({ ...essentialIds })) as {
        status: number
        data: { data: { graph: { date: string; orderCount: number }[] } }
      }
      if (res.status === 200) {
        const graphData = res.data.data.graph.reverse()
        setOrdersData(
          graphData.map((d: { date: string; orderCount: number }) => ({
            label: d.date,
            value: d.orderCount,
          })),
        )
      } else {
        toast.error('Something Went Wrong')
      }
    } catch {
      toast.error('Failed to load orders graph')
    }
    setLoadingOrders(false)
  }, [essentialIds])

  const getSitesData = useCallback(async () => {
    setLoadingSites(true)
    try {
      const res = (await fetchSitesCompletedPerWeek({ ...essentialIds })) as {
        status: number
        data: { data: { graph: { date: string; orderCount: number }[] } }
      }
      if (res.status === 200) {
        const graphData = res.data.data.graph.reverse()
        setSitesData(
          graphData.map((d: { date: string; orderCount: number }) => ({
            label: d.date,
            value: d.orderCount,
          })),
        )
      } else {
        toast.error('Something Went Wrong')
      }
    } catch {
      toast.error('Failed to load sites graph')
    }
    setLoadingSites(false)
  }, [essentialIds])

  useEffect(() => {
    if (defPayload.customerId && defPayload.siteId.length > 0) {
      setTimeout(() => {
        getOrdersData()
        getSitesData()
      }, 0)
    }
  }, [
    defPayload.customerId,
    defPayload.siteId.length,
    getOrdersData,
    getSitesData,
  ])

  const renderSummaryCard = (cardId: string) => {
    switch (cardId) {
      case 'orders-closed':
        if (!isWidgetVisible('summary_order_closed_per_week')) return null
        return (
          <SortableTabCard key={cardId} id={cardId} className='h-full'>
            {loadingOrders ? (
              <Skelton />
            ) : (
              <AnalyticsCard
                title='Order Closed Per Week'
                data={
                  ordersData.length > 1
                    ? ordersData
                    : [{ value: 0 }, { value: 0 }]
                }
                filterOptions={[]}
                lineColor='#EF4444'
                gradientFrom='rgba(239, 68, 68, 0.9)'
                gradientTo='rgba(239, 68, 68, 0.05)'
                handleExternalPageIcon={() =>
                  navigate('/service-delivery/orders')
                }
              />
            )}
          </SortableTabCard>
        )
      case 'sites-completed':
        if (!isWidgetVisible('summary_site_completed_per_week')) return null
        return (
          <SortableTabCard key={cardId} id={cardId} className='h-full'>
            {loadingSites ? (
              <Skelton />
            ) : (
              <AnalyticsCard
                title='Site Completed Per Week'
                data={
                  sitesData.length > 1
                    ? sitesData
                    : [{ value: 0 }, { value: 0 }]
                }
                filterOptions={[]}
                lineColor='#10B981'
                gradientFrom='rgba(16, 185, 129, 0.9)'
                gradientTo='rgba(16, 185, 129, 0.05)'
                handleExternalPageIcon={() =>
                  navigate('/service-delivery/sites')
                }
              />
            )}
          </SortableTabCard>
        )
      case 'orders-due':
        if (!isWidgetVisible('summary_order_due_by_date')) return null
        return (
          <SortableTabCard
            key={cardId}
            id={cardId}
            className='h-full xl:col-span-2'
          >
            <OrdersDueTable />
          </SortableTabCard>
        )
      default:
        return null
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleSummaryDragEnd}
    >
      <SortableContext items={summaryCards} strategy={rectSortingStrategy}>
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
          {summaryCards.map(renderSummaryCard)}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default SummaryTab

const Skelton = () => {
  return (
    <div
      className={`bg-primary border border-border border-l-[4px] border-l-accent rounded-xl shadow-sm p-4 w-full h-full flex flex-col`}
    >
      {/* Header Skeleton */}
      <div className='mb-4 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <GradiantSkelton width='w-36' height='h-7' className='!rounded-md' />
          <GradiantSkelton width='w-5' height='h-5' className='!rounded-sm' />
        </div>
        <GradiantSkelton width='w-28' height='h-8' className='!rounded-md' />
      </div>

      {/* Chart Area */}
      <div className='relative w-full flex-1 min-h-0 pt-3'>
        <svg
          viewBox='0 0 500 120'
          preserveAspectRatio='none'
          className='w-full h-full text-border'
        >
          <defs>
            <linearGradient id='skelton-chart-grad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='currentColor' stopOpacity='0.15' />
              <stop offset='100%' stopColor='currentColor' stopOpacity='0' />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          <line
            x1='0'
            y1='20'
            x2='500'
            y2='20'
            stroke='currentColor'
            strokeOpacity='0.1'
            strokeDasharray='3 3'
          />
          <line
            x1='0'
            y1='50'
            x2='500'
            y2='50'
            stroke='currentColor'
            strokeOpacity='0.1'
            strokeDasharray='3 3'
          />
          <line
            x1='0'
            y1='80'
            x2='500'
            y2='80'
            stroke='currentColor'
            strokeOpacity='0.1'
            strokeDasharray='3 3'
          />
          <line
            x1='0'
            y1='110'
            x2='500'
            y2='110'
            stroke='currentColor'
            strokeOpacity='0.1'
            strokeDasharray='3 3'
          />

          {/* Wavy Area */}
          <path
            d='M 0 90 C 30 85, 40 40, 60 40 C 80 40, 90 95, 110 95 C 130 95, 140 85, 160 85 C 180 85, 190 90, 210 90 C 230 90, 240 30, 260 30 C 280 30, 290 95, 310 95 C 330 95, 340 90, 360 90 C 380 90, 390 35, 410 35 C 430 35, 440 90, 460 90 C 480 90, 490 85, 500 85 L 500 120 L 0 120 Z'
            fill='url(#skelton-chart-grad)'
            className='animate-pulse'
          />

          {/* Wavy Line */}
          <path
            d='M 0 90 C 30 85, 40 40, 60 40 C 80 40, 90 95, 110 95 C 130 95, 140 85, 160 85 C 180 85, 190 90, 210 90 C 230 90, 240 30, 260 30 C 280 30, 290 95, 310 95 C 330 95, 340 90, 360 90 C 380 90, 390 35, 410 35 C 430 35, 440 90, 460 90 C 480 90, 490 85, 500 85'
            fill='none'
            stroke='currentColor'
            strokeOpacity='0.25'
            strokeWidth='2'
            className='animate-pulse'
          />
        </svg>
      </div>

      {/* X-axis labels */}
      <div className='mt-2 flex justify-between px-2'>
        {[1, 2, 3, 4, 5, 6].map(item => (
          <GradiantSkelton
            key={item}
            width='w-14'
            height='h-3'
            className='!rounded-sm'
          />
        ))}
      </div>
    </div>
  )
}
